import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, Search, Eye, Calendar, MapPin } from 'lucide-react';

export default function MyParticipantsPage() {
  const navigate = useNavigate();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadMyParticipants();
  }, []);

  const loadMyParticipants = async () => {
    setLoading(true);
    try {
      // Get shifts assigned to current staff to find their participants
      const shiftsRes = await api.get('/roster/my-shifts');
      const shifts = shiftsRes.data.items || shiftsRes.data.data || [];
      
      // Extract unique participant IDs from shifts
      const participantIds = [...new Set(shifts
        .filter(s => s.participant_id)
        .map(s => s.participant_id))];
      
      // Fetch participant details for each unique participant
      const participantPromises = participantIds.map(id => 
        api.get(`/participants/${id}`).catch(() => null)
      );
      
      const results = await Promise.all(participantPromises);
      const validParticipants = results
        .filter(r => r?.data?.data)
        .map(r => r.data.data);
      
      setParticipants(validParticipants);
    } catch (error) {
      console.error('Failed to load participants:', error);
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  const filteredParticipants = participants.filter(p => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      p.first_name?.toLowerCase().includes(searchLower) ||
      p.last_name?.toLowerCase().includes(searchLower) ||
      p.ndis_number?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="my-participants-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6" />
          My Participants
        </h1>
        <p className="text-muted-foreground mt-1">
          Participants you support based on your assigned shifts
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or NDIS number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="participant-search"
        />
      </div>

      {/* Participants List */}
      {filteredParticipants.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-muted-foreground font-medium">
              {search ? 'No participants match your search' : 'No participants assigned to you yet'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Participants will appear here once you have shifts assigned with them
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredParticipants.map((participant) => (
            <Card 
              key={participant.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/participants/${participant.id}`)}
              data-testid={`participant-card-${participant.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {participant.first_name} {participant.last_name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {participant.ndis_number || 'No NDIS number'}
                    </p>
                  </div>
                  <Badge variant={participant.status === 'active' ? 'default' : 'secondary'}>
                    {participant.status || 'Active'}
                  </Badge>
                </div>
                
                <div className="space-y-2 text-sm text-muted-foreground mb-4">
                  {participant.phone && (
                    <p>Phone: {participant.phone}</p>
                  )}
                  {participant.address && (
                    <p className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {participant.address.length > 40 
                        ? participant.address.substring(0, 40) + '...' 
                        : participant.address}
                    </p>
                  )}
                </div>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/participants/${participant.id}`);
                  }}
                  data-testid={`view-participant-${participant.id}`}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Profile
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">Note</p>
        <p>
          This list shows participants from your assigned shifts. Contact your coordinator 
          if you need access to other participants.
        </p>
      </div>
    </div>
  );
}
